// InvitationAccept.jsx — Page publique d'acceptation d'invitation
// Route : /invitation/:token
import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/lib/auth';
import { toast } from 'sonner';
import { Loader2, ArrowRight, CheckCircle2, Sparkles, Mail, Gift } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InvitationAccept() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ prenom: '', nom: '', mot_de_passe: '', telephone: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/invitation/${token}`)
      .then((r) => setData(r.data))
      .catch((err) => setError(err?.response?.data?.error || 'Invitation introuvable'))
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.mot_de_passe.length < 6) return toast.error('Mot de passe trop court (min 6)');
    setSubmitting(true);
    try {
      const { data: result } = await axios.post(`${API}/public/invitation/${token}/accepter`, form);
      localStorage.setItem('assoperf_token', result.token);
      localStorage.setItem('assoperf_user', JSON.stringify(result.user));
      setUser(result.user);
      toast.success(`Bienvenue ${result.user.prenom} !`);
      // Full reload to ensure AuthContext + ProtectedRoute pick up the fresh user
      window.location.href = '/espace';
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Erreur');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F9F8] grid place-items-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#94A3B8]" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-[#F9F9F8] grid place-items-center p-4">
        <div className="bg-white border border-[#E2E8F0] rounded-3xl p-9 text-center max-w-md">
          <h1 className="font-display text-3xl font-bold text-[#0F172A] mb-3">
            Invitation <span className="italic font-medium text-[#475569]">indisponible.</span>
          </h1>
          <p className="text-sm text-[#475569] mb-6">{error || 'Lien invalide ou expiré.'}</p>
          <Link to="/login" className="btn-primary"><ArrowRight className="w-4 h-4" /> Aller à la connexion</Link>
        </div>
      </div>
    );
  }

  const { asso, formule, invitation } = data;
  const couleur = asso.couleur_principale || '#0047FF';

  return (
    <div className="min-h-screen bg-[#F9F9F8] text-[#0F172A] font-sans antialiased grid lg:grid-cols-2" style={{ '--theme-primary': couleur }} data-testid="invitation-page">

      {/* Volet asso (gauche) */}
      <aside className="hidden lg:flex flex-col justify-between p-12 xl:p-16 text-white relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${couleur} 0%, ${shade(couleur, -30)} 100%)` }}>
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-white/10 blur-3xl" />
        <div className="relative">
          {asso.logo_url ? (
            <img src={asso.logo_url} alt={asso.nom} className="w-16 h-16 rounded-2xl bg-white p-2 mb-8" />
          ) : (
            <div className="w-16 h-16 rounded-2xl bg-white grid place-items-center font-display font-black text-2xl mb-8" style={{ color: couleur }}>
              {asso.nom?.[0]?.toUpperCase()}
            </div>
          )}
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 backdrop-blur text-[10px] font-bold uppercase tracking-[0.2em] mb-6">
            <Sparkles className="w-3 h-3" /> Invitation
          </div>
          <h2 className="font-display text-5xl xl:text-6xl font-black tracking-tight leading-[0.98] mb-5">
            Rejoignez <span className="italic font-medium">{asso.nom}.</span>
          </h2>
          <p className="text-base xl:text-lg text-white/80 leading-relaxed max-w-md">
            {asso.description_publique || `${invitation.invited_by_name} vous invite à rejoindre ${asso.nom}. En 30 secondes, créez votre compte et accédez à l'espace membre.`}
          </p>
        </div>
        <div className="relative text-xs text-white/60">
          {asso.ville && `${asso.ville} · `}{asso.type}
        </div>
      </aside>

      {/* Formulaire (droite) */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">

          {/* Header mobile */}
          <div className="lg:hidden flex items-center gap-3 mb-8">
            {asso.logo_url ? (
              <img src={asso.logo_url} alt={asso.nom} className="w-10 h-10 rounded-xl object-cover" />
            ) : (
              <div className="w-10 h-10 rounded-xl grid place-items-center text-white font-display font-black text-sm" style={{ background: couleur }}>{asso.nom?.[0]}</div>
            )}
            <div>
              <div className="font-display font-bold text-[#0F172A]">{asso.nom}</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#94A3B8] font-bold">Invitation</div>
            </div>
          </div>

          <div className="mb-8">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] mb-3" style={{ color: couleur }}>Acceptation</div>
            <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight text-[#0F172A] leading-[1.02] mb-3">
              Votre <span className="italic font-medium" style={{ color: couleur }}>compte.</span>
            </h1>
            <div className="text-sm text-[#475569] flex items-center gap-1.5">
              <Mail className="w-3.5 h-3.5" /> {invitation.email}
            </div>
          </div>

          {/* Message perso */}
          {invitation.message_perso && (
            <div className="mb-6 p-5 rounded-2xl border-l-4" style={{ background: `${couleur}0d`, borderLeftColor: couleur }} data-testid="invitation-message">
              <div className="text-[10px] font-bold uppercase tracking-[0.14em] mb-2" style={{ color: couleur }}>Message de {invitation.invited_by_name}</div>
              <p className="text-sm text-[#0F172A] leading-relaxed whitespace-pre-wrap">{invitation.message_perso}</p>
            </div>
          )}

          {/* Formule proposée */}
          {formule && (
            <div className="mb-6 p-5 rounded-2xl bg-white border border-[#E2E8F0] flex items-center gap-4" data-testid="invitation-formule">
              <div className="w-11 h-11 rounded-xl grid place-items-center shrink-0" style={{ background: `${couleur}19`, color: couleur }}>
                <Gift className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#475569] mb-0.5">Formule proposée</div>
                <div className="font-display font-bold text-[#0F172A] truncate">{formule.nom}</div>
              </div>
              <div className="font-display text-2xl font-black tracking-tight" style={{ color: couleur }}>
                {formule.prix_cts === 0 ? 'Gratuit' : `${(formule.prix_cts / 100).toFixed(0)}€`}
                {formule.periode_label && <span className="text-xs font-normal text-[#475569] ml-1">{formule.periode_label}</span>}
              </div>
            </div>
          )}

          <form onSubmit={submit} className="space-y-4" data-testid="invitation-form">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-[0.14em] text-[#475569] mb-2">Prénom *</label>
                <input required type="text" value={form.prenom} onChange={(e) => setForm({ ...form, prenom: e.target.value })} className="ap-input" data-testid="invitation-prenom" />
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-[0.14em] text-[#475569] mb-2">Nom *</label>
                <input required type="text" value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} className="ap-input" data-testid="invitation-nom" />
              </div>
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.14em] text-[#475569] mb-2">Téléphone (optionnel)</label>
              <input type="tel" value={form.telephone} onChange={(e) => setForm({ ...form, telephone: e.target.value })} className="ap-input" placeholder="06 12 34 56 78" />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.14em] text-[#475569] mb-2">Mot de passe (6+ caractères) *</label>
              <input required type="password" minLength={6} value={form.mot_de_passe} onChange={(e) => setForm({ ...form, mot_de_passe: e.target.value })} className="ap-input" placeholder="••••••••" data-testid="invitation-password" />
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="group w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-2xl text-white font-bold text-base transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0"
              style={{ background: couleur }}
              data-testid="invitation-submit"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                <>
                  <CheckCircle2 className="w-4 h-4" />
                  Accepter et créer mon compte
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                </>
              )}
            </button>
          </form>

          <p className="text-center text-xs text-[#94A3B8] mt-6">
            En continuant, vous acceptez les{' '}
            <Link to="/cgu" className="underline hover:text-[#0F172A]">CGU</Link> et la{' '}
            <Link to="/confidentialite" className="underline hover:text-[#0F172A]">politique de confidentialité</Link>.
          </p>
        </div>
      </div>
    </div>
  );
}

function shade(hex, percent) {
  const n = parseInt(hex.replace('#', ''), 16);
  const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
  const adj = (c) => Math.max(0, Math.min(255, Math.round(c + c * (percent / 100))));
  return `rgb(${adj(r)}, ${adj(g)}, ${adj(b)})`;
}
